export const toDateInputValue = (value) => {
  if (!value) {
    return "";
  }

  const str = String(value).trim();
  if (!str || /^invalid date$/i.test(str)) {
    return "";
  }

  if (/^\d{4}-\d{2}-\d{2}/.test(str)) {
    return str.slice(0, 10);
  }

  const dmYdash = str.match(/^(\d{1,2})-(\d{1,2})-(\d{4})/);
  if (dmYdash) {
    const day = dmYdash[1].padStart(2, "0");
    const month = dmYdash[2].padStart(2, "0");
    return `${dmYdash[3]}-${month}-${day}`;
  }

  const dmYslash = str.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (dmYslash) {
    const day = dmYslash[1].padStart(2, "0");
    const month = dmYslash[2].padStart(2, "0");
    return `${dmYslash[3]}-${month}-${day}`;
  }

  const parsed = new Date(str);
  if (!Number.isNaN(parsed.getTime())) {
    return formatLocalYmd(parsed);
  }

  return "";
};

const formatLocalYmd = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

/** Persist pump configuration date from bootstrap API responses. */
export const rememberPumpConfigDate = (configDate) => {
  const normalized = toDateInputValue(configDate);
  if (normalized) {
    localStorage.setItem("CONFG_DATE", normalized);
  }
  return normalized;
};

export const getPumpConfigDate = (configDate) =>
  toDateInputValue(configDate) || toDateInputValue(localStorage.getItem("CONFG_DATE"));

export const isOperationStarted = (statOperation) => statOperation === "Yes";

/** Current operational date from /operational-dates (header chip / check_meta_date). */
export const getCurrentOperationalDate = () =>
  toDateInputValue(localStorage.getItem("m_op_date")) ||
  toDateInputValue(localStorage.getItem("op_date")) ||
  "";

/**
 * Default opening-balance ac_date when the user leaves the date unset.
 * Pre-operation: day before pump config date (existing wizard behaviour).
 * Post-operation: current operational date.
 */
export const getDefaultOpeningBalanceAcDate = ({
  statOperation,
  configDate,
} = {}) => {
  if (isOperationStarted(statOperation)) {
    return getCurrentOperationalDate();
  }

  return getOpeningBalanceMinDate(configDate);
};

/**
 * Opening-balance datepicker earliest selectable day = pump config date − 1.
 */
export const getOpeningBalanceMinDate = (configDate) => {
  const base = getPumpConfigDate(configDate);
  if (!base) {
    return "";
  }

  const [year, month, day] = base.split("-").map(Number);
  if (!year || !month || !day) {
    return "";
  }

  const date = new Date(year, month - 1, day);
  date.setDate(date.getDate() - 1);
  return formatLocalYmd(date);
};

export const formatOpeningBalanceDisplayDate = (value) => {
  const inputValue = toDateInputValue(value);
  if (!inputValue) {
    return "";
  }

  const [year, month, day] = inputValue.split("-");
  return `${day}-${month}-${year}`;
};

export const formatOperationalDate = formatOpeningBalanceDisplayDate;

/**
 * Opening-balance field editability.
 * Free during first-time config; after start-operation, requires pump unlock by Owner/Super Admin.
 * Day-close on the OB date must NOT hide/block fields once the pump is unlocked
 * (backend unlock already authorizes Owner/Manager edits).
 */
export const canEditOpeningBalanceFields = ({
  statOperation,
  openingBalanceUnlocked = false,
} = {}) => {
  if (statOperation === "Yes") {
    return Boolean(openingBalanceUnlocked);
  }
  return true;
};

/** Show the unlock control only after operations have started. */
export const shouldShowOpeningBalanceUnlock = (statOperation) =>
  statOperation === "Yes";

/**
 * Opening-balance date picker: hidden during initial pump configuration.
 * After operations start, shown only when owner/manager has unlocked OB edits.
 */
export const shouldShowOpeningBalanceDateField = ({
  statOperation,
  openingBalanceUnlocked = false,
} = {}) => {
  if (statOperation !== "Yes") {
    return false;
  }

  return canEditOpeningBalanceFields({ statOperation, openingBalanceUnlocked });
};
